import React, { createContext, useContext, useMemo } from 'react';
import en from './en.json';
import zh from './zh.json';

type Strings = typeof en;

const locales: Record<string, Strings> = { en, zh };

// Detect language from hostname: cn.journalfeeds.com → 'zh', everything else → 'en'
function detectLang(): string {
  const host = window.location.hostname;
  if (host.startsWith('cn.')) return 'zh';
  return 'en';
}

interface I18nContextValue {
  lang: string;
  t: Strings;
  /** The ?lang= query param value to append to API calls */
  apiLang: string;
  /** Whether this is the Chinese site */
  isChinese: boolean;
}

const I18nContext = createContext<I18nContextValue>({
  lang: 'en',
  t: en,
  apiLang: 'en',
  isChinese: false,
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo(() => {
    const lang = detectLang();
    return {
      lang,
      t: locales[lang] || en,
      apiLang: lang,
      isChinese: lang === 'zh',
    };
  }, []);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

/** Helper: append ?lang=zh to a URL if we're on the Chinese site */
export function localizeApiUrl(url: string, lang: string): string {
  if (lang === 'en') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}lang=${lang}`;
}
